import type { Collision, CollisionDetection } from '@dnd-kit/core';
import { closestCenter, pointerWithin } from '@dnd-kit/core';
import type {
  NodePath,
  TreeGroupNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import {
  parseContainerId,
  parseInsertId,
  parseItemId,
  pathsEqual,
} from '@/components/tree-dnd/tree-dnd-path';
import { getNodeAtPath } from '@/components/tree-dnd/tree-dnd-utils';

export const treeCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return closestCenter(args);
};

export function getDeepestContainerPath(
  collisions: Collision[]
): NodePath | null {
  const containerPaths = collisions
    .map((collision) => parseContainerId(collision.id))
    .filter((path): path is NodePath => path !== null)
    .sort((a, b) => b.length - a.length);

  return containerPaths[0] ?? null;
}

type DropTarget =
  | { type: 'insert'; parentPath: NodePath; index: number }
  | { type: 'nest'; path: NodePath }
  | { type: 'before-item'; path: NodePath };

function pickDeepestInsertSlot(
  collisions: Collision[]
): { parentPath: NodePath; index: number } | null {
  const slots = collisions
    .map((collision) => parseInsertId(collision.id))
    .filter(
      (slot): slot is { parentPath: NodePath; index: number } => slot !== null
    )
    .sort((a, b) => b.parentPath.length - a.parentPath.length);

  return slots[0] ?? null;
}

export function resolveDropTarget(
  collisions: Collision[],
  activePath: NodePath,
  tree: TreeGroupNode
): DropTarget | null {
  const insertSlot = pickDeepestInsertSlot(collisions);
  if (insertSlot) {
    return { type: 'insert', ...insertSlot };
  }

  const itemPaths = collisions
    .map((collision) => parseItemId(collision.id))
    .filter((path): path is NodePath => path !== null)
    .filter((path) => !pathsEqual(path, activePath))
    .sort((a, b) => b.length - a.length);

  const deepestItemPath = itemPaths[0];
  if (deepestItemPath) {
    const itemNode = getNodeAtPath(tree, deepestItemPath);
    if (itemNode && isGroupNode(itemNode)) {
      return { type: 'nest', path: deepestItemPath };
    }
    return { type: 'before-item', path: deepestItemPath };
  }

  const containerPaths = collisions
    .map((collision) => parseContainerId(collision.id))
    .filter((path): path is NodePath => path !== null)
    .sort((a, b) => b.length - a.length);

  for (const containerPath of containerPaths) {
    const node = getNodeAtPath(tree, containerPath);
    if (node && isGroupNode(node) && node.children.length === 0) {
      return { type: 'insert', parentPath: containerPath, index: 0 };
    }
  }

  return null;
}
